import {
  Badge,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { getLegalNameAnnotation } from "features/commitments/CommitmentUtils";
import { CommitmentTabProps } from "features/commitments/view/tabs/CommitmentTabProps";
import { useSecurityClassListSuspense } from "gen/endpoints/fund-security-classes/fund-security-classes";
import { useInvestorClassListSuspense } from "gen/endpoints/investor-class/investor-class";
import {
  useSecurityOwnershipCancelCorrectListSuspense,
  useSecurityOwnershipRetrieveSuspense,
} from "gen/endpoints/security-ownership/security-ownership";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { SecurityOwnershipCancelCorrectReadData } from "gen/model/securityOwnershipCancelCorrectReadData";
import { SecurityOwnershipCancelCorrectReadDataList } from "gen/model/securityOwnershipCancelCorrectReadDataList";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { AuditLogIcon } from "shared/icons/AuditLogIcon";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import invariant from "shared/utils/TinyInvariant";
import { useBoolean } from "usehooks-ts";

export const CommitmentTabProfile: React.FC = () => {
  const { t } = useTranslation([
    "investorEntity",
    "entity",
    "commitment",
    "common",
  ]);

  const { fundFamilyId, commitmentId } =
    useOutletContext<Required<CommitmentTabProps>>();

  const {
    value: isCancelCorrectModalOpen,
    setTrue: openCancelCorrecttModal,
    setFalse: closeCancelCorrectModal,
  } = useBoolean();

  const { data: fundSecurityData } = useSecurityClassListSuspense(fundFamilyId);
  const { data: investorClassData } =
    useInvestorClassListSuspense(fundFamilyId);
  const { data: commitmentData } = useSecurityOwnershipRetrieveSuspense(
    fundFamilyId,
    commitmentId,
  );

  const amountText = formatMoney(commitmentData.commitment_amount);

  const securityClassText = getLegalNameAnnotation(
    fundSecurityData.find((it) => it.id === commitmentData.security_class_id),
  );

  const commitmentClassText = investorClassData.find(
    (it) => it.id === commitmentData.investor_class_id,
  )?.name;

  const { data: cancelCorrectData } =
    useSecurityOwnershipCancelCorrectListSuspense(fundFamilyId, commitmentId);

  return (
    <PageTabBody data-private={true}>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("commitment:commitmentAmount")}>
                {amountText}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:fund")}>
                {securityClassText}
              </Descriptions.Item>
              <Descriptions.Item label={t("common:commitmentClass")}>
                {commitmentClassText}
                {!!cancelCorrectData.length && (
                  <Tooltip label="View Cancel Correct Class History">
                    <IconButton
                      aria-label="cancel-correct"
                      size="sm"
                      variant="ghost"
                      ml={2}
                      icon={
                        <AuditLogIcon
                          color="gray.400"
                          height="15px"
                          width="15px"
                        />
                      }
                      onClick={openCancelCorrecttModal}
                    />
                  </Tooltip>
                )}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {isCancelCorrectModalOpen && (
        <CancelCorrectModal
          cancelCorrectData={cancelCorrectData}
          onCloseComplete={closeCancelCorrectModal}
        />
      )}
    </PageTabBody>
  );
};

const CancelCorrectModal: React.FC<{
  onCloseComplete?: () => void;
  cancelCorrectData: SecurityOwnershipCancelCorrectReadDataList;
}> = ({ onCloseComplete, cancelCorrectData }) => {
  const { t } = useTranslation(["commitment", "common"]);
  const { value: isOpen, setFalse: close } = useBoolean(true);
  const cancelRef = useRef(null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalBody>
          <TableBox isFullWidth={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("commitment:cancelCorrect.cancelCorrectClassHistory")}
              </TableBoxTitle>
              <ModalCloseButton />
            </TableBoxHeader>
            <TableBoxBody>
              <TableContainer height="100%" overflowY="auto">
                <Table>
                  <Thead>
                    <Tr>
                      <Th>{t("common:effectiveDate")}</Th>
                      <Th>
                        {t("commitment:cancelCorrect.editClass.oldClass")}
                      </Th>
                      <Th>
                        {t("commitment:cancelCorrect.editClass.newClass")}
                      </Th>
                      <Th>{t("common:state")}</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cancelCorrectData.map((cancelCorrect, index: number) => (
                      <CancelCorrectRow
                        key={index}
                        cancelCorrect={cancelCorrect}
                      />
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </TableBoxBody>
          </TableBox>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CancelCorrectRow: React.FC<{
  cancelCorrect: SecurityOwnershipCancelCorrectReadData;
}> = ({ cancelCorrect }) => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const onClick = () => {
    if (
      cancelCorrect.event_state == FundFamilyEventStateMachineFieldsEnum.draft
    ) {
      navigate(
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .commitments({})
          .cancelCorrect({
            commitmentId,
            fundFamilyEventId: cancelCorrect.event_id,
          }).$,
      );
    } else {
      navigate(
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .commitments({})
          .cancelCorrectHistorical({
            commitmentId,
            fundFamilyEventId: cancelCorrect.event_id,
          }).$,
      );
    }
  };
  const { fundFamilyId, commitmentId } =
    useOutletContext<Required<CommitmentTabProps>>();
  const badge = getCapitalActivityEventStateBadge(cancelCorrect.event_state, t);
  invariant(badge);
  return (
    <Tr>
      <Td>{formatIsoDateToDisplay(cancelCorrect.effective_date)}</Td>
      <Td>{cancelCorrect.previous_investor_class_name}</Td>
      <Td>{cancelCorrect.new_investor_class_name}</Td>
      <Td>
        <Badge colorScheme={badge.type}>{badge.text}</Badge>
      </Td>
      <Td>
        <Button variant="ghost" onClick={onClick}>
          {t("common:view")}
        </Button>
      </Td>
    </Tr>
  );
};
